import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { api } from '../api';
import { Aviso, Etiqueta, Painel, type TomEtiqueta } from '../comum';

/**
 * O que este produto sabe fazer contra este KACE.
 *
 * 🔴 **Existe para que a incerteza não vire folclore.** Sem uma tela que diga
 * `managedInstall.write = UNKNOWN` com a evidência ao lado, a pergunta "dá para
 * instalar software?" é respondida de cabeça — "acho que sim" — e a promessa
 * entra numa proposta comercial antes de alguém sondar o appliance.
 *
 * 🔴 **`UNKNOWN` e `NOT_IMPLEMENTED` têm cores diferentes de propósito.** Um diz
 * "não apuramos" e tem conserto; o outro diz "não existe" e não tem. Pintar os
 * dois de cinza faria a lista de pendências parecer trabalho perdido.
 */

interface Capacidade {
  readonly nome: string;
  readonly estado: 'SUPPORTED' | 'PARTIAL' | 'UNKNOWN' | 'NOT_IMPLEMENTED';
  readonly rotulo: string;
  readonly evidencia: string;
  readonly pendencias: readonly string[];
  readonly subconjunto?: readonly string[];
}

interface Resposta {
  readonly itens: readonly Capacidade[];
  readonly resumo: Readonly<Record<string, number>>;
}

const ESTADO: Readonly<
  Record<Capacidade['estado'], { rotulo: string; tom: TomEtiqueta; explica: string }>
> = {
  SUPPORTED: {
    rotulo: 'Disponível',
    tom: 'ok',
    explica: 'Caminho fechado e exercitado. Sujeito a permissão e aprovação como qualquer outro.',
  },
  PARTIAL: {
    rotulo: 'Parcial',
    tom: 'alerta',
    explica: 'Parte funciona e foi comprovada; o resto não. Só o subconjunto abaixo é planejado.',
  },
  UNKNOWN: {
    rotulo: 'Não apurado',
    tom: 'erro',
    explica:
      'O KACE talvez suporte — não sabemos. O produto NÃO executa e não promete. ' +
      'Tem conserto: as pendências abaixo dizem o que falta medir.',
  },
  NOT_IMPLEMENTED: {
    rotulo: 'Indisponível',
    tom: 'neutro',
    explica: 'Não existe caminho, ou o produto não o construiu. Não é planejado.',
  },
};

export function Capacidades({ aoPerderSessao }: { aoPerderSessao: () => void }): ReactNode {
  const [dados, setDados] = useState<Resposta | null>(null);

  const carregar = useCallback(async (): Promise<void> => {
    try {
      setDados(await api.get<Resposta>('/api/capacidades'));
    } catch (e: unknown) {
      if ((e as { precisaLogin?: boolean }).precisaLogin === true) aoPerderSessao();
    }
  }, [aoPerderSessao]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  if (dados === null) return null;

  // Ordem: o que exige atenção primeiro. Uma lista alfabética esconderia o
  // UNKNOWN no meio de nove capacidades que funcionam.
  const ordem: Capacidade['estado'][] = ['UNKNOWN', 'PARTIAL', 'NOT_IMPLEMENTED', 'SUPPORTED'];
  const ordenadas = [...dados.itens].sort(
    (a, b) => ordem.indexOf(a.estado) - ordem.indexOf(b.estado),
  );

  return (
    <Painel
      titulo="Capacidades"
      sub="O que este produto sabe fazer contra este appliance, e com que evidência."
    >
      <Aviso tipo="info">
        Esta lista responde <strong>uma</strong> pergunta: a capacidade existe e está
        disponível? Quem pode usá-la continua sendo decidido pela permissão do KACE, e o
        que exige aprovação humana continua exigindo.
      </Aviso>

      <p className="suave">
        {dados.resumo['SUPPORTED']} disponível(is) · {dados.resumo['PARTIAL']} parcial(is) ·{' '}
        {dados.resumo['UNKNOWN']} não apurada(s) · {dados.resumo['NOT_IMPLEMENTED']}{' '}
        indisponível(is)
      </p>

      {ordenadas.map((c) => {
        const e = ESTADO[c.estado];
        return (
          <div
            key={c.nome}
            style={{ borderBottom: '1px solid var(--borda)', paddingBottom: 12, marginBottom: 12 }}
          >
            <div className="linha">
              <span>
                <strong>{c.rotulo}</strong>{' '}
                <span className="suave mono" style={{ fontSize: '0.85em' }}>
                  {c.nome}
                </span>
              </span>
              <Etiqueta tom={e.tom} titulo={e.explica}>
                {e.rotulo}
              </Etiqueta>
            </div>
            <p className="suave">{e.explica}</p>

            {c.subconjunto !== undefined && c.subconjunto.length > 0 && (
              <p className="suave">
                <strong>Comprovado:</strong> {c.subconjunto.join('; ')}.
              </p>
            )}

            {/*
              A evidência fica atrás de um `details` porque é longa, mas nunca
              escondida: um estado sem evidência visível é palpite com aparência
              de fato, e quem lê precisa poder conferir de onde veio.
            */}
            <details>
              <summary className="suave">Como isto foi apurado</summary>
              <p className="suave">{c.evidencia}</p>
              {c.pendencias.length > 0 && (
                <>
                  <p className="suave">
                    <strong>O que falta:</strong>
                  </p>
                  <ul>
                    {c.pendencias.map((p, i) => (
                      <li key={i} className="suave">
                        {p}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </details>
          </div>
        );
      })}
    </Painel>
  );
}
